import React, { useMemo, useCallback } from 'react';
import clsx from 'clsx';
import styles from './MagicSignTable.module.scss';
import muStyles from '../../../MagicUpload/components/MagicUploadFilesTable/MagicUploadFilesTable.module.scss';
import { MagicSignRow, GlobalMagicSignRow, MagicSignStatus, SignType, ConfigMeta, MagicSignFilters, Signatory } from '../../types';
import { MAGIC_SIGN_STATUS_CONFIG, SIGN_TYPE_OPTIONS, SIGN_TYPE_LABELS } from '../../constants';
import CircularSelect from '../../../MagicUpload/components/CircularSelect/CircularSelect';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';
import { formatDateByFormat, DATE_FORMATS } from '../../../../utils/dateUtils';
import SortToggle from './Components/SortToggle';
import ColumnFilterDropdown from './Components/ColumnFilterDropdown';
import StatusCell from './Components/StatusCell';
import ThreeDotsMenu from './Components/ThreeDotsMenu';
import SignatoryInlineDropdown from './Components/SignatoryInlineDropdown';
import OrgInlineSelect from './Components/OrgInlineSelect';
import { fileIconDataRoom } from '../../../../assets/hostedassets';

interface MagicSignTableProps {
    rows: MagicSignRow[];
    loading: boolean;
    configMeta: ConfigMeta | null;
    activeRowId: string | null;
    highlightedRowId: string | null;
    /** Optional org column — used in global view */
    showOrgColumn?: boolean;
    /** Active server-side filters — drives column filter chip states */
    activeFilters?: MagicSignFilters;
    /** Called when user changes column filters or sort; parent re-fetches */
    onFiltersChange?: (filters: MagicSignFilters) => void;
    onRowClick: (row: MagicSignRow) => void;
    onUpdateDocType: (id: string, docType: string) => void;
    onUpdateSignType: (id: string, signType: SignType) => void;
    /** Inline dropdown saves directly — receives id + full signatory list */
    onSaveSignatories: (id: string, signatories: Signatory[]) => void;
    /** Generates (or re-generates) signing links for a row */
    onGenerateLinks: (row: MagicSignRow) => void;
    /** Opens the email composer from the 3-dot menu */
    onSendEmail: (row: MagicSignRow) => void;
    onViewAuditLog: (row: MagicSignRow) => void;
    onDownloadDocument: (row: MagicSignRow) => void;
    onCopySigningLinks: (row: MagicSignRow) => void;
    onCancelRequest: (id: string) => void;
    /** PNO id — needed by the global org-search cell */
    pnoId?: string;
    /** Global view: assign/change the company on a row */
    onAssociateOrg?: (rowId: string, orgId: string) => void;
    /**
     * Per-PNO Polus doc-type list (same list Magic Upload's Data Type column uses), fetched once
     * by the owning hook (useMagicSignLogic / useGlobalMagicSignLogic) and passed down — keeps
     * this table presentational instead of firing its own fetch.
     */
    polusDocTypes?: string[];
}

const MagicSignTable: React.FC<MagicSignTableProps> = ({
    rows,
    loading,
    configMeta,
    activeRowId,
    highlightedRowId,
    showOrgColumn = false,
    activeFilters = {},
    onFiltersChange,
    onRowClick,
    onUpdateDocType,
    onUpdateSignType,
    onSaveSignatories,
    onGenerateLinks,
    onSendEmail,
    onViewAuditLog,
    onDownloadDocument,
    onCopySigningLinks,
    onCancelRequest,
    pnoId,
    onAssociateOrg,
    polusDocTypes = [],
}) => {
    // Document type labels for CircularSelect (string[]) — Polus list (fetched once by the owning
    // hook), else configMeta fallback.
    const docTypeLabels: string[] = useMemo(
        () => (polusDocTypes.length ? polusDocTypes : configMeta?.documentTypes?.map((dt) => dt.label) ?? []),
        [polusDocTypes, configMeta?.documentTypes],
    );

    // Sign type labels for CircularSelect (string[])
    const signTypeLabels: string[] = useMemo(() => {
        const enabledSignTypes = configMeta?.signTypes ?? null;
        return SIGN_TYPE_OPTIONS.filter((o) => {
            // Config not loaded yet: only E-Sign is ever available without backend opt-in
            // (Aadhaar/DSC must never appear unless the backend explicitly enables them).
            if (!enabledSignTypes) return o.id === SignType.E_SIGN;
            // Show a sign type ONLY if the backend listed it as enabled. An option absent from
            // enabledSignTypes (e.g. AADHAAR/DSC when Leegality is gated off) is hidden.
            const found = enabledSignTypes.find((st) => st.id === o.id);
            return !!found && found.enabled;
        }).map((o) => o.label);
    }, [configMeta?.signTypes]);

    // Doc type is stored as the label itself (matching Magic Upload's plain-label model), so the
    // label↔stored-value mapping is identity. configMeta lookup is kept only as a legacy fallback.
    const docTypeLabelToId = useCallback((label: string): string => label, []);

    // Helper: map label → SignType for sign types
    const signTypeLabelToId = useCallback((label: string): SignType => {
        const found = SIGN_TYPE_OPTIONS.find((o) => o.label === label);
        return (found?.id as SignType) ?? (label as SignType);
    }, []);

    // Helper: resolve selected doc type label for a row. documentType now stores the label; the
    // configMeta lookup covers legacy rows that stored an id.
    const docTypeLabelForRow = useCallback(
        (row: MagicSignRow): string | undefined => {
            if (!row.documentType) return undefined;
            const found = configMeta?.documentTypes?.find((dt) => dt.id === row.documentType);
            return found?.label ?? row.documentType;
        },
        [configMeta?.documentTypes],
    );

    // Helper: resolve selected sign type label for a row. Uses SIGN_TYPE_LABELS (which maps both
    // AADHAAR and the legacy DSC value to "Aadhaar/DSC") so any stored value renders correctly.
    const signTypeLabelForRow = useCallback((row: MagicSignRow): string | undefined => {
        if (!row.signType) return undefined;
        return SIGN_TYPE_LABELS[row.signType as SignType] ?? row.signType;
    }, []);

    // Document Type, Sign Type and Signatory are editable ONLY while the document is in UPLOADED.
    // Once links are generated (GENERATED_LINKS onwards) every column field locks — to change the
    // configuration the user must cancel and re-upload. This matches the reviewed behaviour:
    // generating the links freezes the document's configuration.
    const isRowLocked = useCallback((row: MagicSignRow): boolean => row.status !== MagicSignStatus.UPLOADED, []);

    const colCount = showOrgColumn ? 9 : 8;

    // Status filter options — driven by all persisted statuses (GENERATING_LINKS is a client-only
    // transient state and is never a real filter value, so it is excluded).
    const statusOptions = useMemo(
        () =>
            Object.values(MagicSignStatus)
                .filter((s) => s !== MagicSignStatus.GENERATING_LINKS)
                .map((s) => ({
                    id: s,
                    label: MAGIC_SIGN_STATUS_CONFIG[s]?.label ?? s,
                })),
        [],
    );

    const handleFilterColumn = useCallback(
        (
            key: keyof Pick<MagicSignFilters, 'documentTypes' | 'signTypes' | 'statuses' | 'requestedBy' | 'orgIds' | 'signatoryEmails'>,
            values: string[],
        ) => {
            if (!onFiltersChange) return;
            const next: MagicSignFilters = { ...activeFilters, [key]: values.length ? values : undefined };
            onFiltersChange(next);
        },
        [activeFilters, onFiltersChange],
    );

    // Column-funnel options are derived from the loaded rows (deduplicated) — mirrors how
    // Magic Upload scopes its Organisation / Uploaded By funnels to the visible dataset.
    // They populate fully once the auto-applied "my requests" filter is cleared.
    const requesterOptions = useMemo(() => {
        const seen = new Map<string, string>();
        rows.forEach((r) => {
            if (r.requestedByUserId && !seen.has(r.requestedByUserId)) {
                seen.set(r.requestedByUserId, r.requestedByName ?? r.requestedByUserId);
            }
        });
        return Array.from(seen, ([id, label]) => ({ id, label }));
    }, [rows]);

    const orgOptions = useMemo(() => {
        const seen = new Map<string, string>();
        rows.forEach((r) => {
            const gr = r as GlobalMagicSignRow;
            if (gr.orgId && !seen.has(gr.orgId)) {
                seen.set(gr.orgId, gr.orgName ?? gr.orgId);
            }
        });
        return Array.from(seen, ([id, label]) => ({ id, label }));
    }, [rows]);

    const signatoryOptions = useMemo(() => {
        const seen = new Map<string, string>();
        rows.forEach((r) => {
            (r.signatories ?? []).forEach((s) => {
                if (s.email && !seen.has(s.email)) {
                    seen.set(s.email, s.name ?? s.email);
                }
            });
        });
        return Array.from(seen, ([id, label]) => ({ id, label }));
    }, [rows]);

    const handleSort = useCallback(
        (field: 'updatedAt' | 'fileName' | 'status', dir: 'asc' | 'desc' | undefined) => {
            if (!onFiltersChange) return;
            const next: MagicSignFilters = { ...activeFilters, sortBy: dir ? field : undefined, sortDir: dir };
            onFiltersChange(next);
        },
        [activeFilters, onFiltersChange],
    );

    // Client-side reorder: bubble the just-created (highlighted) row to the top so the user
    // can immediately see and act on the new entry. This is a transient presentational effect
    // for entry-point highlighting only — it is NOT a persistent sort and is not sent to the server.
    const displayRows = useMemo(() => {
        if (!highlightedRowId) return rows;
        const idx = rows.findIndex((r) => r.id === highlightedRowId);
        if (idx <= 0) return rows;
        const reordered = [...rows];
        const [highlighted] = reordered.splice(idx, 1);
        return [highlighted, ...reordered];
    }, [rows, highlightedRowId]);

    return (
        <div className={styles.TableWrapper}>
            <table className={clsx(muStyles.MagicUploadTable, styles.Table)}>
                <thead>
                    <tr className={muStyles.MUHeader}>
                        <th>
                            <SortToggle
                                label="File Name"
                                field="fileName"
                                currentSortBy={activeFilters.sortBy}
                                currentSortDir={activeFilters.sortDir}
                                onSort={handleSort}
                            />
                        </th>
                        {showOrgColumn && (
                            <th>
                                <ColumnFilterDropdown
                                    label="Organisation Name"
                                    options={orgOptions}
                                    selected={(activeFilters.orgIds as string[]) ?? []}
                                    onApply={(vals) => handleFilterColumn('orgIds', vals)}
                                />
                            </th>
                        )}
                        <th>
                            <ColumnFilterDropdown
                                label="Document Type"
                                options={docTypeLabels.map((l) => ({ id: l, label: l }))}
                                selected={(activeFilters.documentTypes as string[]) ?? []}
                                onApply={(vals) => handleFilterColumn('documentTypes', vals)}
                            />
                        </th>
                        <th>
                            <ColumnFilterDropdown
                                label="Signatory"
                                options={signatoryOptions}
                                selected={(activeFilters.signatoryEmails as string[]) ?? []}
                                onApply={(vals) => handleFilterColumn('signatoryEmails', vals)}
                            />
                        </th>
                        <th>
                            <ColumnFilterDropdown
                                label="Sign Type"
                                options={
                                    SIGN_TYPE_OPTIONS.filter((o) => {
                                        // Mirror the inline-cell gate: only show a sign type the backend
                                        // explicitly enabled; default to E-Sign when config isn't loaded.
                                        // (Absent from enabledSignTypes => hidden — Aadhaar/DSC stay gated.)
                                        const enabled = configMeta?.signTypes ?? null;
                                        if (!enabled) return o.id === SignType.E_SIGN;
                                        const found = enabled.find((st) => st.id === o.id);
                                        return !!found && found.enabled;
                                    }) as { id: string; label: string }[]
                                }
                                selected={(activeFilters.signTypes as string[]) ?? []}
                                onApply={(vals) => handleFilterColumn('signTypes', vals)}
                            />
                        </th>
                        <th>
                            <ColumnFilterDropdown
                                label="Requested By"
                                options={requesterOptions}
                                selected={(activeFilters.requestedBy as string[]) ?? []}
                                onApply={(vals) => handleFilterColumn('requestedBy', vals)}
                            />
                        </th>
                        <th>
                            <SortToggle
                                label="Updated on"
                                field="updatedAt"
                                currentSortBy={activeFilters.sortBy}
                                currentSortDir={activeFilters.sortDir}
                                onSort={handleSort}
                            />
                        </th>
                        <th>
                            <ColumnFilterDropdown
                                label="Status"
                                options={statusOptions}
                                selected={(activeFilters.statuses as string[]) ?? []}
                                onApply={(vals) => handleFilterColumn('statuses', vals)}
                            />
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {loading && rows.length === 0 ? (
                        <tr className={styles.LoadingRow}>
                            <td colSpan={colCount}>
                                <LoadingSpinner height="2rem" color="var(--primary-text-colour)" />
                            </td>
                        </tr>
                    ) : displayRows.length === 0 ? (
                        <tr>
                            <td colSpan={colCount} className={styles.EmptyState}>
                                No documents queued for signing. Drag and drop a file above to get started.
                            </td>
                        </tr>
                    ) : (
                        displayRows.map((row) => {
                            // Cast once to GlobalMagicSignRow to access optional org fields
                            // when showOrgColumn is true; harmless for non-global rows.
                            const globalRow = row as GlobalMagicSignRow;
                            return (
                                <tr
                                    key={row.id}
                                    className={clsx(muStyles.MURow, styles.TbodyRow, {
                                        [styles.ActiveRow]: activeRowId === row.id,
                                        [styles.HighlightedRow]: highlightedRowId === row.id,
                                    })}
                                    onClick={() => onRowClick(row)}
                                >
                                    <td>
                                        <div className={styles.FileNameCell}>
                                            <img src={fileIconDataRoom} alt="" className={styles.FileIcon} />
                                            <span className={styles.FileName} title={row.fileName}>
                                                {row.fileName}
                                            </span>
                                        </div>
                                    </td>
                                    {showOrgColumn && (
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <OrgInlineSelect
                                                rowId={row.id}
                                                pnoId={pnoId ?? ''}
                                                orgName={globalRow.orgName}
                                                locked={isRowLocked(row)}
                                                onAssociate={(rowId, orgId) => onAssociateOrg?.(rowId, orgId)}
                                            />
                                        </td>
                                    )}
                                    <td onClick={(e) => e.stopPropagation()}>
                                        {isRowLocked(row) ? (
                                            <span className={styles.LockedCellText} title={docTypeLabelForRow(row) ?? ''}>
                                                {docTypeLabelForRow(row) ?? '-'}
                                            </span>
                                        ) : (
                                            <div className={styles.InlineDropdownDocType}>
                                                <CircularSelect
                                                    placeholder="Search"
                                                    option={docTypeLabels}
                                                    selectedOption={docTypeLabelForRow(row)}
                                                    allowAddNew={false}
                                                    isFinalized={row.status === MagicSignStatus.UPLOADING}
                                                    onSelect={async (label) => {
                                                        onUpdateDocType(row.id, docTypeLabelToId(label));
                                                        return true;
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        {/* Always the dropdown — it renders read-only (disabled checkboxes) once locked. */}
                                        <SignatoryInlineDropdown row={row} configMeta={configMeta} onSave={onSaveSignatories} />
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        {isRowLocked(row) ? (
                                            <span className={styles.LockedCellText} title={signTypeLabelForRow(row) ?? '-'}>
                                                {signTypeLabelForRow(row) ?? '-'}
                                            </span>
                                        ) : (
                                            <div className={styles.InlineDropdown}>
                                                <CircularSelect
                                                    placeholder="Select"
                                                    option={signTypeLabels}
                                                    selectedOption={signTypeLabelForRow(row)}
                                                    // Non-searchable trigger renders the placeholder in a plain span; tint it the
                                                    // same reddish placeholder colour as the Signatory cell when nothing is picked.
                                                    inputStyles={signTypeLabelForRow(row) ? undefined : { color: '#df666a' }}
                                                    allowAddNew={false}
                                                    searchable={false}
                                                    isFinalized={row.status === MagicSignStatus.UPLOADING}
                                                    onSelect={async (label) => {
                                                        onUpdateSignType(row.id, signTypeLabelToId(label));
                                                        return true;
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td>{row.requestedByName ?? '-'}</td>
                                    <td className={styles.UpdatedOnCell}>
                                        {row.updatedAt
                                            ? formatDateByFormat(row.updatedAt, DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_a_DD_MMM_YYYY)
                                            : '-'}
                                    </td>
                                    <td className={styles.StatusTd}>
                                        <StatusCell row={row} configMeta={configMeta} onCopySigningLinks={onCopySigningLinks} />
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <ThreeDotsMenu
                                            row={row}
                                            onGenerateLinks={onGenerateLinks}
                                            onSendEmail={onSendEmail}
                                            onViewAuditLog={onViewAuditLog}
                                            onDownloadDocument={onDownloadDocument}
                                            onCopySigningLinks={onCopySigningLinks}
                                            onCancelRequest={onCancelRequest}
                                        />
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default MagicSignTable;
